/**
 * 回测系统页面
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSetCurrentPage, useAppStore } from '../stores/useAppStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import {
  backtestService,
  Strategy,
  BacktestConfig,
  BacktestResult,
  BacktestTaskStatus,
} from '../services/backtestService';
import { BarInterval, StrategyCode } from '@/services/enums';
import { COMMON_TIMEFRAMES } from '../constants/timeframes';
import PageHeader from '../components/PageHeader';

export default function Backtest() {
  const setCurrentPage = useSetCurrentPage();
  const playbackSymbol = useAppStore((state) => state.playbackSymbol);
  const playbackRange = useAppStore((state) => state.playbackRange);

  // 策略和交易对数据
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(true);

  // 回测配置 - 交易对固定为K线回放页面的交易对
  const [selectedStrategy, setSelectedStrategy] = useState<StrategyCode | ''>('');
  const selectedSymbol = playbackSymbol || 'BTC-USDT';
  const [timeframe, setTimeframe] = useState<BarInterval>('1H');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [initialCapital, setInitialCapital] = useState(10000);
  const [leverage, setLeverage] = useState(1);
  const [positionSize, setPositionSize] = useState(0.5);
  const [strategyParams, setStrategyParams] = useState<Record<string, any>>({});

  // 回测状态
  const [running, setRunning] = useState(false);
  const [taskStatus, setTaskStatus] = useState<BacktestTaskStatus | null>(null);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingStoppedRef = useRef(false); // 防止重复处理完成状态

  // 历史回测列表
  const [backtestList, setBacktestList] = useState<BacktestResult[]>([]);
  const [listLoading, setListLoading] = useState(false);

  // 当前Tab
  const [activeTab, setActiveTab] = useState<'new' | 'history'>('new');
  const hasLoadedRef = useRef(false);

  // 加载策略
  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    async function loadData() {
      try {
        const strategiesData = await backtestService.getStrategies();
        const strategies = strategiesData || [];
        setStrategies(strategies);

        // 设置默认值
        if (strategies.length > 0) {
          setSelectedStrategy(strategies[0].code);
          setStrategyParams(strategies[0].defaultParams);
        }
      } catch (err) {
        console.error('Load data error:', err);
        setError('加载数据失败');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // 如果来自K线回放的可见范围，带入时间粒度和日期范围
  useEffect(() => {
    if (playbackRange) {
      setTimeframe(playbackRange.bar as BarInterval);
      const fromDate = new Date(playbackRange.from * 1000);
      const toDate = new Date(playbackRange.to * 1000);
      setStartDate(fromDate.toISOString().split('T')[0]);
      setEndDate(toDate.toISOString().split('T')[0]);
    }
  }, [playbackRange]);

  // 加载历史回测列表
  const loadBacktestList = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await backtestService.getBacktestList({ page: 1, pageSize: 20 });
      setBacktestList(response.data);
    } catch (err) {
      console.error('Load backtest list error:', err);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      loadBacktestList();
    }
  }, [activeTab, loadBacktestList]);

  // 切换策略时更新默认参数
  useEffect(() => {
    if (selectedStrategy) {
      const strategy = strategies.find((s) => s.code === selectedStrategy);
      if (strategy) {
        setStrategyParams(strategy.defaultParams);
      }
    }
  }, [selectedStrategy, strategies]);

  // 清理轮询
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // 轮询任务状态
  const pollTaskStatus = useCallback(async (backtestId: number) => {
    // 如果已经停止轮询，直接返回
    if (isPollingStoppedRef.current) {
      return;
    }

    try {
      const taskStatus = await backtestService.getBacktestStatus(backtestId);

      // 再次检查，防止并发请求
      if (isPollingStoppedRef.current) {
        return;
      }

      setTaskStatus(taskStatus);

      if (taskStatus.status === 'completed' || taskStatus.status === 'failed') {
        // 标记已停止，防止其他并发请求继续处理
        isPollingStoppedRef.current = true;

        // 停止轮询
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }

        if (taskStatus.status === 'completed') {
          setResult(taskStatus.result);
        } else {
          setError(taskStatus.error || '回测执行失败');
        }
        setRunning(false);
      }
    } catch (err) {
      console.error('Poll status error:', err);
      isPollingStoppedRef.current = true;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      setRunning(false);
    }
  }, []);

  // 执行回测
  const handleRunBacktest = async () => {
    if (!selectedStrategy || !selectedSymbol || !startDate || !endDate) {
      setError('请填写完整的回测配置');
      return;
    }

    setRunning(true);
    setError('');
    setResult(null);
    setTaskStatus(null);
    isPollingStoppedRef.current = false; // 重置停止标志

    try {
      // 将日期固定为 UTC 当天起止，避免本地时区导致的日期偏移
      const startIso = `${startDate}T00:00:00.000Z`;
      const endIso = `${endDate}T23:59:59.999Z`;

      const config: BacktestConfig = {
        strategyCode: selectedStrategy,
        strategyParams,
        symbol: selectedSymbol,
        startTime: startIso,
        endTime: endIso,
        timeframe,
        initialCapital,
        leverage,
        positionSize,
      };

      // 异步执行回测
      const res = await backtestService.runBacktest(config);

      // 开始轮询状态
      setTaskStatus({
        backtestId: res.backtestId,
        status: 'pending',
        message: '等待执行...',
      });

      // 每2秒轮询一次
      pollingRef.current = setInterval(() => {
        pollTaskStatus(res.backtestId);
      }, 2000);

      // 立即查询一次
      pollTaskStatus(res.backtestId);
    } catch (err: any) {
      console.error('Run backtest error:', err);
      setError(err.response?.data?.error || '回测执行失败');
      setRunning(false);
    }
  };

  // 删除回测
  const handleDelete = async (id: number) => {
    if (!window.confirm('确定要删除这条回测记录吗？')) return;

    try {
      await backtestService.deleteBacktest(id);
      loadBacktestList();
    } catch (err) {
      console.error('Delete backtest error:', err);
    }
  };

  // 获取当前策略信息
  const currentStrategy = strategies.find((s) => s.code === selectedStrategy);

  return (
    <GradientBackground>
      <div className="min-h-screen">
        <ParticleBackground />

        {/* 页面头部 */}
        <PageHeader title="策略回测" onBack={() => setCurrentPage('kline-playback')} />

        {/* 内容区域 */}
        <div className="pt-14 px-4 pb-4">
          {/* Tab切换 */}
          <div className="flex gap-2 mb-4 mt-2">
            <button
              onClick={() => setActiveTab('new')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition ${
                activeTab === 'new' ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              新建回测
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition ${
                activeTab === 'history' ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              历史记录
            </button>
          </div>

          {/* 加载状态 */}
          {loading ? (
            <div className="pt-20 flex justify-center">
              <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : activeTab === 'new' ? (
            <div className="space-y-4">
              {/* 配置面板 */}
              <div className="bg-white/5 backdrop-blur-md rounded-xl p-4 border border-white/10">
                <h2 className="text-base font-semibold text-white mb-3">回测配置</h2>

                {/* 策略选择 */}
                <div className="mb-3">
                  <label className="block text-sm text-gray-400 mb-1">策略</label>
                  <select
                    value={selectedStrategy}
                    onChange={(e) => setSelectedStrategy(e.target.value as StrategyCode)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                  >
                    {strategies.map((s) => (
                      <option key={s.code} value={s.code} className="bg-gray-800">
                        {s.name}
                      </option>
                    ))}
                  </select>
                  {currentStrategy && <p className="mt-1 text-xs text-gray-500">{currentStrategy.description}</p>}
                </div>

                {/* 交易对（固定） */}
                <div className="mb-3">
                  <label className="block text-sm text-gray-400 mb-1">交易对</label>
                  <div className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                    {selectedSymbol}
                  </div>
                </div>

                {/* 时间周期 */}
                <div className="mb-3">
                  <label className="block text-sm text-gray-400 mb-1">时间周期</label>
                  <select
                    value={timeframe}
                    onChange={(e) => setTimeframe(e.target.value as BarInterval)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                  >
                    {COMMON_TIMEFRAMES.map((t) => (
                      <option key={t.value} value={t.value} className="bg-gray-800">
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 时间范围 */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">开始日期</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">结束日期</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                    />
                  </div>
                </div>

                {/* 资金配置 */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">初始资金</label>
                    <input
                      type="number"
                      value={initialCapital}
                      onChange={(e) => setInitialCapital(Number(e.target.value))}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-2 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">杠杆</label>
                    <input
                      type="number"
                      value={leverage}
                      onChange={(e) => setLeverage(Number(e.target.value))}
                      min={1}
                      max={100}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-2 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">仓位</label>
                    <input
                      type="number"
                      value={positionSize}
                      onChange={(e) => setPositionSize(Number(e.target.value))}
                      step={0.1}
                      min={0.1}
                      max={1}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-2 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                    />
                  </div>
                </div>

                {/* 策略参数配置 */}
                {currentStrategy && Object.keys(strategyParams).length > 0 && (
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm text-gray-400">策略参数</label>
                      <button
                        type="button"
                        onClick={() => {
                          if (currentStrategy) {
                            setStrategyParams(currentStrategy.defaultParams);
                          }
                        }}
                        className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
                      >
                        重置为默认值
                      </button>
                    </div>
                    <div className="border-t border-white/10 mb-3"></div>
                    <div className="space-y-3">
                      {Object.entries(strategyParams).map(([key, value]) => {
                        const description = currentStrategy.paramDescriptions?.[key] || key;
                        const paramType = typeof value;

                        // 判断是否是枚举类型（priceSource等）
                        const isEnum = key === 'priceSource' || key.includes('Source') || key.includes('Type');
                        const enumOptions =
                          isEnum && key === 'priceSource'
                            ? ['close', 'open', 'high', 'low', 'hl2', 'hlc3', 'ohlc4']
                            : [];

                        return (
                          <div key={key}>
                            <label className="block text-xs text-gray-400 mb-1">{description}</label>
                            {paramType === 'number' ? (
                              <input
                                type="number"
                                value={value as number}
                                onChange={(e) => {
                                  const newValue = e.target.value === '' ? 0 : Number(e.target.value);
                                  setStrategyParams((prev) => ({ ...prev, [key]: newValue }));
                                }}
                                step={Math.abs(value as number) < 1 ? 0.01 : 1}
                                className="w-full bg-white/10 border border-white/20 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                              />
                            ) : paramType === 'boolean' ? (
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={value as boolean}
                                  onChange={(e) => {
                                    setStrategyParams((prev) => ({ ...prev, [key]: e.target.checked }));
                                  }}
                                  className="w-4 h-4 bg-white/10 border-white/20 rounded text-purple-600 focus:ring-purple-500 focus:ring-2"
                                />
                                <span className="ml-2 text-xs text-gray-400">{value ? '是' : '否'}</span>
                              </div>
                            ) : isEnum && enumOptions.length > 0 ? (
                              <select
                                value={value as string}
                                onChange={(e) => {
                                  setStrategyParams((prev) => ({ ...prev, [key]: e.target.value }));
                                }}
                                className="w-full bg-white/10 border border-white/20 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                              >
                                {enumOptions.map((opt) => (
                                  <option key={opt} value={opt} className="bg-gray-800">
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                value={value as string}
                                onChange={(e) => {
                                  setStrategyParams((prev) => ({ ...prev, [key]: e.target.value }));
                                }}
                                className="w-full bg-white/10 border border-white/20 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* 错误提示 */}
                {error && (
                  <div className="mb-3 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}

                {/* 执行按钮 */}
                <button
                  onClick={handleRunBacktest}
                  disabled={running}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:opacity-90 disabled:opacity-50 transition flex items-center justify-center gap-2"
                >
                  {running ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>{taskStatus?.message || '回测中...'}</span>
                    </>
                  ) : (
                    '开始回测'
                  )}
                </button>
              </div>

              {/* 结果面板 */}
              <div className="bg-white/5 backdrop-blur-md rounded-xl p-4 border border-white/10">
                <h2 className="text-base font-semibold text-white mb-3">回测结果</h2>

                {result ? (
                  <div className="space-y-3">
                    {/* 收益指标 */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white/5 rounded-lg p-3">
                        <div className="text-xs text-gray-400">总收益率</div>
                        <div
                          className={`text-lg font-bold ${result.totalReturnPct >= 0 ? 'text-green-400' : 'text-red-400'}`}
                        >
                          {result.totalReturnPct?.toFixed(2)}%
                        </div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3">
                        <div className="text-xs text-gray-400">年化收益</div>
                        <div
                          className={`text-lg font-bold ${result.annualReturn >= 0 ? 'text-green-400' : 'text-red-400'}`}
                        >
                          {result.annualReturn?.toFixed(2)}%
                        </div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3">
                        <div className="text-xs text-gray-400">最大回撤</div>
                        <div className="text-lg font-bold text-red-400">{result.maxDrawdown?.toFixed(2)}%</div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3">
                        <div className="text-xs text-gray-400">夏普比率</div>
                        <div className="text-lg font-bold text-white">{result.sharpeRatio?.toFixed(2)}</div>
                      </div>
                    </div>

                    {/* 交易统计 */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-white/5 rounded-lg p-2 text-center">
                        <div className="text-xs text-gray-400">总交易</div>
                        <div className="text-base font-bold text-white">{result.totalTrades}</div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-2 text-center">
                        <div className="text-xs text-gray-400">胜率</div>
                        <div className="text-base font-bold text-white">{result.winRate?.toFixed(1)}%</div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-2 text-center">
                        <div className="text-xs text-gray-400">盈亏比</div>
                        <div className="text-base font-bold text-white">{result.profitFactor?.toFixed(2)}</div>
                      </div>
                    </div>

                    {/* 其他指标 */}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">盈利次数</span>
                        <span className="text-green-400">{result.winTrades}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">亏损次数</span>
                        <span className="text-red-400">{result.lossTrades}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">最终资金</span>
                        <span className="text-white">${result.finalCapital?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">持仓时间</span>
                        <span className="text-white">{result.avgHoldingTime?.toFixed(0)}分</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-8">配置参数后点击"开始回测"查看结果</div>
                )}
              </div>
            </div>
          ) : (
            /* 历史记录 */
            <div className="bg-white/5 backdrop-blur-md rounded-xl p-4 border border-white/10">
              <h2 className="text-base font-semibold text-white mb-3">历史回测记录</h2>

              {listLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                </div>
              ) : backtestList.length === 0 ? (
                <div className="text-center text-gray-500 py-8">暂无回测记录</div>
              ) : (
                <div className="space-y-2">
                  {backtestList.map((bt) => (
                    <div key={bt.id} className="bg-white/5 rounded-lg p-3 border border-white/10">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="text-white font-medium text-sm">{bt.strategy?.name || bt.name}</div>
                          <div className="text-gray-400 text-xs">
                            {bt.symbol} · {bt.timeframe}
                          </div>
                        </div>
                        <span
                          className={`px-2 py-0.5 rounded text-xs ${
                            bt.status === 'completed'
                              ? 'bg-green-500/20 text-green-400'
                              : bt.status === 'running'
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : bt.status === 'failed'
                                  ? 'bg-red-500/20 text-red-400'
                                  : 'bg-gray-500/20 text-gray-400'
                          }`}
                        >
                          {bt.status === 'completed'
                            ? '完成'
                            : bt.status === 'running'
                              ? '运行中'
                              : bt.status === 'failed'
                                ? '失败'
                                : '等待'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex gap-4 text-xs">
                          <span
                            className={`${Number(bt.totalReturnPct || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}
                          >
                            收益: {Number(bt.totalReturnPct || 0).toFixed(2)}%
                          </span>
                          <span className="text-gray-400">胜率: {Number(bt.winRate || 0).toFixed(1)}%</span>
                          <span className="text-gray-400">夏普: {Number(bt.sharpeRatio || 0).toFixed(2)}</span>
                        </div>
                        <button onClick={() => handleDelete(bt.id)} className="text-red-400 hover:text-red-300 text-xs">
                          删除
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </GradientBackground>
  );
}
